import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import type {
  MetadataState,
  SetFolderNamePayload,
  SetFolderNamesPayload,
  SetFilenamePayload,
  SetCategoryPayload,
  SetClientPayload,
  SetAccountPayload,
  SetClientIdPayload,
  SetAccountIdPayload,
  SetTemplateHtmlByIdPayload,
  RemoveTemplateHtmlByIdPayload,
} from "./metadataTypes";

const initialState: MetadataState = {
  folderName: "",
  folderNames: [],
  filename: "",
  category: "",
  client: "",
  account: "",
  clientId: "",
  accountId: "",
  selectedFolder: "",
  templateHtmlById: {},
};

const metadataSlice = createSlice({
  name: "metadata",
  initialState,
  reducers: {
    // Generic updater - preferred for new code
    updateMetadata: (state, action: PayloadAction<Partial<MetadataState>>) => {
      Object.assign(state, action.payload);
    },
    // Individual setters - kept for backward compatibility
    setFolderName: (state, action: PayloadAction<SetFolderNamePayload>) => {
      state.folderName = action.payload.folderName;
    },
    setFolderNames: (state, action: PayloadAction<SetFolderNamesPayload>) => {
      state.folderNames = action.payload.folderNames;
    },
    setFilename: (state, action: PayloadAction<SetFilenamePayload>) => {
      state.filename = action.payload.filename;
    },
    setCategory: (state, action: PayloadAction<SetCategoryPayload>) => {
      state.category = action.payload.category;
    },
    setClient: (state, action: PayloadAction<SetClientPayload>) => {
      state.client = action.payload.client;
    },
    setAccount: (state, action: PayloadAction<SetAccountPayload>) => {
      state.account = action.payload.account;
    },
    setClientId: (state, action: PayloadAction<SetClientIdPayload>) => {
      state.clientId = action.payload.clientId;
    },
    setAccountId: (state, action: PayloadAction<SetAccountIdPayload>) => {
      state.accountId = action.payload.accountId;
    },
    setTemplateHtmlById: (
      state,
      action: PayloadAction<SetTemplateHtmlByIdPayload>,
    ) => {
      state.templateHtmlById[action.payload.templateId] = action.payload.html;
    },
    removeTemplateHtmlById: (
      state,
      action: PayloadAction<RemoveTemplateHtmlByIdPayload>,
    ) => {
      delete state.templateHtmlById[action.payload.templateId];
    },
    clearMetadata: (state) => {
      state.folderName = "";
      state.folderNames = [];
      state.filename = "";
      state.category = "";
      state.client = "";
      state.account = "";
      state.clientId = "";
      state.accountId = "";
      state.selectedFolder = "";
      state.templateHtmlById = {};
    },
  },
});

export const {
  updateMetadata,
  setFolderName,
  setFolderNames,
  setFilename,
  setCategory,
  setClient,
  setAccount,
  setClientId,
  setAccountId,
  setTemplateHtmlById,
  removeTemplateHtmlById,
  clearMetadata,
} = metadataSlice.actions;

// Selectors
export const selectFolderNames = (state: RootState) => state.metadata.folderNames;
export const selectFolderName = (state: RootState) => state.metadata.folderName;
export const selectSelectedFolder = (state: RootState) => state.metadata.selectedFolder;
export const selectCategory = (state: RootState) => state.metadata.category;
export const selectAccountId = (state: RootState) => state.metadata.accountId;
export const selectClientId = (state: RootState) => state.metadata.clientId;
export const selectFilename = (state: RootState) => state.metadata.filename;
export const selectAccount = (state: RootState) => state.metadata.account;
export const selectClient = (state: RootState) => state.metadata.client;
export const selectTemplateHtmlById = (state: RootState) => state.metadata.templateHtmlById;

export const metadataReducer = metadataSlice.reducer;
